import Utils from "./Utils";
import * as nominatim from 'nominatim-client';

// Campos que no entran en el histórico offline, estén a la profundidad que estén.
const CLAVES_IGNORADAS = [
    'guardadoLocal',
    'guardadoSilencioso',
    'historicoOffline',
    'tiempoRealAutomatico',
    'sesiones',
    'sesionesManuales',
    'ruta',
    'accordionOpened',
    'erroresAuditoria',
    'listErrorsForLabel'
];

function DeepChangesObject() {
    this.diffs = [];
    this.snapshotValue = function(val) {
        if (val instanceof Date) {
            return val.toISOString();
        }
        if (Array.isArray(val)) {
            return val.map(v => this.snapshotValue(v));
        }
        if (val && typeof val === "object") {
            const out = {};
            Object.keys(val).forEach(k => {
                out[k] = this.snapshotValue(val[k]);
            });
            return out;
        }
        return val;
    };
    this.push = function (diff) {
        let valL = diff.lhs;
        let valR = diff.rhs;
        const path = Array.isArray(diff.path) ? diff.path : [];
        let pathJoin = path.join(".");
        if (valL instanceof Date) {
            valL = valL.toISOString();
        }
        if (valR instanceof Date) {
            valR = valR.toISOString();
        }
        
        // Ignorar cambios donde ambos valores son equivalentes a "vacío" (null, undefined, "", 0)
        // pero permitir cambios reales de un valor numérico a otro
        const isEmptyL = valL === null || valL === undefined || valL === '';
        const isEmptyR = valR === null || valR === undefined || valR === '';
        
        // Si ambos son "vacíos", ignorar el cambio
        if (isEmptyL && isEmptyR) {
            return;
        }
        
        const hasIgnoredPath = path.some(p => CLAVES_IGNORADAS.includes(p));
        const hasIdPath = path.some(p => p === '_id');

        if (valL != valR && !hasIgnoredPath && !hasIdPath) {
            
            // Log para debugging - eliminar en producción si no es necesario
            console.log('DeepChanges - Registrando cambio:', {
                path: pathJoin,
                anterior: valL,
                nuevo: valR,
                tipo: diff.kind
            });
            
            this.diffs.push({
                // Guardamos snapshots inmutables para evitar que el histórico cambie
                // retroactivamente si el objeto original se modifica después.
                anterior: this.snapshotValue(valL),
                nuevo: this.snapshotValue(valR),
                path: diff.path
            });
        }
    };
    this.toDatabaseFormat = async function () {
        if (!this.diffs || !this.diffs.length) {
            return null;
        }
        
        // Capturar la fecha INMEDIATAMENTE para mantener el orden cronológico correcto
        // incluso si la geolocalización tarda mucho tiempo
        const timestamp = new Date().toISOString();
        
        let res = {
            anterior: {},
            nuevo: {},
            date: timestamp,
            punto: await Utils.executeTask(10000, this.getLocation(), null)
        };

        // Aplicar de menor a mayor profundidad evita que un set en un path padre
        // sobrescriba cambios más específicos de paths hijos.
        const orderedDiffs = [...this.diffs].sort((a, b) => {
            const depthA = Array.isArray(a.path) ? a.path.length : 0;
            const depthB = Array.isArray(b.path) ? b.path.length : 0;
            if (depthA !== depthB) {
                return depthA - depthB;
            }
            const pathA = Array.isArray(a.path) ? a.path.join('.') : '';
            const pathB = Array.isArray(b.path) ? b.path.join('.') : '';
            return pathA.localeCompare(pathB);
        });

        orderedDiffs.forEach(diff => {
            this.setValue(res, diff.anterior, ['anterior', ...diff.path])
            this.setValue(res, diff.nuevo, ['nuevo', ...diff.path])
        });

        return res;

    };
    
    this.validateHistoryConsistency = function(expediente, historicoOffline) {
        /**
         * Valida que el valor más reciente en historicoOffline coincida con el valor actual del expediente
         * Retorna un objeto con las inconsistencias encontradas
         */
        if (!historicoOffline || historicoOffline.length === 0) {
            return { isValid: true, inconsistencies: [] };
        }
        
        const inconsistencies = [];
        
        // Ordenar por fecha para obtener el más reciente
        const sortedHistory = [...historicoOffline].sort((a, b) => {
            return new Date(b.date) - new Date(a.date);
        });
        
        const latestEntry = sortedHistory[0];
        
        if (!latestEntry || !latestEntry.nuevo) {
            return { isValid: true, inconsistencies: [] };
        }
        
        // Recorrer todos los cambios en el histórico más reciente
        this._validateChanges(latestEntry.nuevo, expediente, '', inconsistencies);
        
        return {
            isValid: inconsistencies.length === 0,
            inconsistencies: inconsistencies,
            lastEntry: latestEntry
        };
    };
    
    this._validateChanges = function(historyValue, expedientValue, path, inconsistencies) {
        const self = this;
        
        if (typeof historyValue !== 'object' || historyValue === null) {
            if (historyValue !== expedientValue) {
                inconsistencies.push({
                    path: path,
                    historyValue: historyValue,
                    expedientValue: expedientValue,
                    type: 'scalar_mismatch'
                });
            }
            return;
        }
        
        Object.keys(historyValue).forEach(key => {
            const newPath = path ? `${path}.${key}` : key;
            const hVal = historyValue[key];
            const eVal = expedientValue ? expedientValue[key] : undefined;
            
            if (typeof hVal === 'object' && hVal !== null) {
                self._validateChanges(hVal, eVal || {}, newPath, inconsistencies);
            } else if (hVal !== eVal) {
                inconsistencies.push({
                    path: newPath,
                    historyValue: hVal,
                    expedientValue: eVal,
                    type: 'value_mismatch'
                });
            }
        });
    };
    this.getLocation = async function() {
        const self = this;
        let location = null;
        try {
            if (navigator && navigator.geolocation) {
                const pos = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000, enableHighAccuracy: true });
                });
                if (pos) {
                    location = {
                        punto: { coordinates: [pos.coords.longitude, pos.coords.latitude], type: "Point", x: pos.coords.longitude, y: pos.coords.latitude },
                        latitud: pos.coords.latitude,
                        longitud: pos.coords.longitude,
                        direccion: pos.coords.longitude + ", " + pos.coords.latitude,
                        fecha: new Date()
                    };

                    if (nominatim) {
                        try {
                            let nomClient = nominatim.default.createClient({ useragent: "SIMECAL-MOVILIDAD" });
                            const res = await nomClient.reverse({ lat: pos.coords.latitude, lon: pos.coords.longitude });
                            location.direccion = res.display_name;
                        } catch (e) {
                            console.log("Error obteniendo direccion de nominatim: ", e);
                        }
                    }
                }
            }
        } catch (e) {
            console.log("Error obteniendo direccion de nominatim: ", e);
        }
        return location;
    };
    this.setValue = function (obj, val, namespaces) {
        val = this.sanitizeJson(val);
        for (var i = 0; i < namespaces.length; i++) {
            if (i == namespaces.length - 1) {
                obj[namespaces[i]] = val;
            } else if (obj[namespaces[i]] == null || typeof obj[namespaces[i]] == "undefined") {
                obj[namespaces[i]] = {};
            }
            obj = obj[namespaces[i]];
        }
    };
    this.sanitizeJson = function (obj) {
        const self = this;
        // Object is an array? Then recursively sanitize each element
        if (Array.isArray(obj)) {
            return obj.map(self.sanitizeJson);
        }
        // Object is a non-null object
        else if (typeof obj === 'object' && obj !== null) {
            // Both '$id' and '$values' properties exist? Sanitize the '$values' property
            if (obj.hasOwnProperty('_id')) {
                delete obj._id;
            }
            Object.keys(obj).forEach(k => {
                if (self) {
                    obj[k] = self.sanitizeJson(obj[k]);
                }
            });
        }
        return obj;
    }

}

/**
 * Prefiltro para `DeepDiff.diff`: no baja a los campos que `push` descartaría de todos modos.
 * Sin él, DeepDiff recorría y comparaba en cada guardado el `historicoOffline` entero y las
 * sesiones para luego tirar todo lo que encontraba ahí.
 */
DeepChangesObject.prefiltro = function (path, key) {
    return key === '_id' || CLAVES_IGNORADAS.includes(key);
};

export default DeepChangesObject;
